import { useState, useCallback, useContext } from 'react';
import { ToastContext } from '@forge/common';

type MutateFn<TData, TResult> = (data: TData) => Promise<TResult>;

interface UseApiMutationOptions<TData, TResult> {
  mutationFn: MutateFn<TData, TResult>;
  onSuccess?: (result: TResult) => void;
  successMessage?: string;
  errorMessage?: string;
}

interface UseApiMutationResult<TData, TResult> {
  mutate: (data: TData) => Promise<TResult | undefined>;
  isSubmitting: boolean;
  error: string | undefined;
  reset: () => void;
}

export const useApiMutation = <TData, TResult = void>(
  options: UseApiMutationOptions<TData, TResult>,
): UseApiMutationResult<TData, TResult> => {
  const { mutationFn, onSuccess, successMessage, errorMessage } = options;
  const toast = useContext(ToastContext);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const mutate = useCallback(
    async (data: TData): Promise<TResult | undefined> => {
      setIsSubmitting(true);
      setError(undefined);

      try {
        const result = await mutationFn(data);

        if (successMessage && toast) {
          toast.addSuccess({ title: successMessage });
        }

        onSuccess?.(result);
        return result;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : errorMessage ?? 'An unexpected error occurred';
        setError(message);

        if (toast) {
          toast.addError({ title: message });
        }

        return undefined;
      } finally {
        setIsSubmitting(false);
      }
    },
    [mutationFn, onSuccess, successMessage, errorMessage, toast],
  );

  const reset = useCallback(() => {
    setError(undefined);
  }, []);

  return { mutate, isSubmitting, error, reset };
};
